import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { Helmet } from "react-helmet";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import MobileNav from "../mobileNav/MobileNav";
import Logo from "../../assets/Opensource-01.png";
import Barcode from "react-barcode";
import Swal from "sweetalert2";
import requestInfo from "../../assets/icons/task-02.png";
import clientInfo from "../../assets/icons/user.png";
import smartAc from "../../assets/icons/smart-ac.png";
import repair from "../../assets/icons/repair.png";
import repairReport from "../../assets/icons/elements.svg";
import SendingIcon from "../../assets/icons/send.svg";
import printing from "../../assets/icons/printing.svg";
import "./requestDetails.css";

const isValidTechReport = (techReport) => techReport.trim() !== "";

const RequestDetails = () => {
  const params = useParams();
  const [requestDetails, setRequestDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warrantyDetailsPopup, setWarrantyDetailsPopup] = useState(null);

  const [techReport, setTechReport] = useState("");
  const [techReportError, setTechReportError] = useState("");

  const imageRef = useRef(null);

  const maxLength = 200;

  const url = `https://api.generalhome.sa/api/maintenance/AllRequestsReportForTechnician`;

  const getRequestDetails = async () => {
    try {
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);
      const response = await axios.post(url, {
        maintenanceAreaIds: maintenanceAreaIds,
        RequestId: params.inProgressId,
      });

      const fixedData = response.data.data.map((item) => {
        // Replace old domain with the new one
        const oldDomain = "https://aircon-api.hiveagency.tech";
        const newDomain = "https://api.generalhome.sa";

        return {
          ...item,
          ImagePath: item.ImagePath?.startsWith(oldDomain)
            ? item.ImagePath.replace(oldDomain, newDomain)
            : item.ImagePath,
        };
      });

      setRequestDetails(fixedData);

      // setRequestDetails(response.data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "تفاصيل الطلب",
    onAfterPrint: () => console.log("تم الطباعة بنجاح"),
  });

  const submit = async (e) => {
    e.preventDefault();
    const techReportIsValid = isValidTechReport(techReport);
    setTechReportError(techReportIsValid ? "" : "برجاء كتابة تقرير الصيانه");
    if (!techReportIsValid) return;
    try {
      const technicianId = localStorage.getItem("UserId");
      const url =
        "https://api.generalhome.sa/api/maintenance/TechnicianAddsHisNote";

      const response = await axios.post(url, {
        TechnicianNotes: techReport,
        RequestId: params.inProgressId,
        technicianId: technicianId,
      });
      console.log(response);
      if (response.data.status === false) {
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: response.data.Message || "حدث خطأ غير متوقع",
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "تم الحفظ",
          text: response.data.Message || "تم إرسال تقرير الصيانة بنجاح",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "فشل في الاتصال",
        text: err.Message || "حدث خطأ أثناء الإرسال",
      });
    }
  };

  const handleDownload = () => {
    if (requestDetails.length > 0 && requestDetails[0].ImagePath) {
      const imageUrl = requestDetails[0].ImagePath;
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = "invoice.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    getRequestDetails();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  const handleShowWarrantyDetails = (maintenanceDetails) => {
    setWarrantyDetailsPopup(maintenanceDetails);
  };

  const handleCloseWarrantyDetails = () => {
    setWarrantyDetailsPopup(null);
  };

  const formatACTypes = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";
    return maintenanceDetails.map((detail) => detail.ACTypeName).join("، ");
  };

  const getWarrantyStatus = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";

    const firstStatus = maintenanceDetails[0].WarrantyStatusName;
    const allSame = maintenanceDetails.every(
      (item) => item.WarrantyStatusName === firstStatus
    );

    return allSame ? firstStatus : "مختلط";
  };

  const normalizeRequestImagePath = (path) => {
    if (!path || typeof path !== "string") return null;

    // Block backend placeholder
    if (path.trim() === "https://api.generalhome.sa/Resources/Images") {
      return null;
    }

    // If it's just a filename → add base URL
    if (!path.startsWith("http")) {
      return `https://api.generalhome.sa/Resources/Images/${path}`;
    }

    try {
      const parsed = new URL(path);

      // Validate image extension
      if (!/\.(jpg|jpeg|png|webp|gif)$/i.test(parsed.pathname)) {
        return null;
      }

      // Force correct domain
      return `https://api.generalhome.sa${parsed.pathname}`;
    } catch {
      return null;
    }
  };

  return (
    <>
      <MobileNav />
      <section className="requestDetails">
        <Helmet>
          <meta charSet="utf-8" />
          <title>تفاصيل الطلب </title>
        </Helmet>
        {loading ? (
          <div className="spinner-container">
            <ClipLoader
              className="loader"
              loading={loading}
              size={100}
              aria-label="Loading Spinner"
              data-testid="loader"
              color="#00b1ff"
            />
          </div>
        ) : (
          <div className="requestDetails-container">
            {requestDetails.map((request) => {
              const warrantyStatus = getWarrantyStatus(
                request.MaintenanceRequestDetails
              );
              return (
                <div className="data-container" key={request.RequestId}>
                  <div className="row g-4">
                    <div className="col-lg-8">
                      <div className="row g-4">
                        <div className="col-lg-12">
                          <div className="request-info">
                            <div className="req-headline">
                              <div className="right-box">
                                <div className="icon">
                                  <img src={requestInfo} alt="requestInfo" />
                                </div>
                                <h5>معلومات الطلب</h5>
                              </div>
                              <div className="left-box">
                                <h6>{request.FormattedRequestNumber}</h6>
                              </div>
                            </div>
                            <div className="req-info">
                              <div className="info-box">
                                <h6>رقم الطلب</h6>
                                <p>{request.FormattedRequestNumber}</p>
                              </div>
                              <div className="info-box">
                                <h6>حالة الطلب</h6>
                                {request.CurrentStatus === "طلبات جديده" ? (
                                  <p className="newReq">جديد</p>
                                ) : request.CurrentStatus ===
                                  "الطلبات التي تم الموافقه عليها" ? (
                                  <p className="approvedReq">تحت المراجعه</p>
                                ) : request.CurrentStatus ===
                                  "الطلبات المرفوضه" ? (
                                  <p className="rejectedReq">مرفوض</p>
                                ) : request.CurrentStatus ===
                                  "المواعيد التي تم جدولتها" ? (
                                  <p className="inProgress">جاري التنفيذ</p>
                                ) : request.CurrentStatus ===
                                  "المواعيد التي تم إلغائها" ? (
                                  <p className="rejectedReq">تم إلغاء الطلب</p>
                                ) : (
                                  <p className="maintainceDone">
                                    تم تنفيذ الطلب
                                  </p>
                                )}
                              </div>
                              <div className="info-box">
                                <h6>تاريخ حجز الطلب:</h6>
                                <p>{request.SubmissionDate}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="client-info">
                            <div className="req-headline">
                              <div className="right-box">
                                <div className="icon">
                                  <img src={clientInfo} alt="requestInfo" />
                                </div>
                                <h5>بيانات العميل</h5>
                              </div>
                            </div>
                            <div className="legend-box">
                              <div className="info-box">
                                <h6>اسم العميل</h6>
                                <p>{request.CustomerName}</p>
                              </div>
                              <div className="info-box">
                                <h6>رقم الهاتف</h6>
                                <p>
                                  <a
                                    href={`https://api.whatsapp.com/send?phone=+966${request.PhoneNumber.replace(
                                      /[^\d+]/g,
                                      ""
                                    )}&text=Hello%2C%20I'm%20interested%20in%20your%20product`}
                                    target="__blank"
                                  >
                                    {request.PhoneNumber}
                                  </a>
                                </p>
                              </div>
                              <div className="info-box">
                                <h6>المدينة</h6>
                                <p>{request.City}</p>
                              </div>
                              <div className="info-box">
                                <h6>المنطقة</h6>
                                <p>{request.Region}</p>
                              </div>
                              <div className="info-box">
                                <h6>الحي</h6>
                                <p>{request.Neighborhood}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row g-4 padding sec-group">
                          <div className="col-lg-6 mt-0 ac-padding">
                            <div className="client-info ac-info">
                              <div className="req-headline">
                                <div className="right-box">
                                  <div className="icon">
                                    <img src={smartAc} alt="smartAc" />
                                  </div>
                                  <h5>بيانات المكيف</h5>
                                </div>
                              </div>
                              <div className="legend-box">
                                <div className="info-box">
                                  <h6>نوع المكيف</h6>
                                  <p>
                                    {" "}
                                    {formatACTypes(
                                      request.MaintenanceRequestDetails
                                    )?.length > maxLength
                                      ? `${formatACTypes(
                                          request.MaintenanceRequestDetails
                                        ).substring(0, maxLength)}...`
                                      : formatACTypes(
                                          request.MaintenanceRequestDetails
                                        )}
                                  </p>
                                </div>
                                <div className="info-box">
                                  <h6>حالة الضمان</h6>
                                  <td
                                    className={
                                      warrantyStatus === "داخل الضمان"
                                        ? "warranty-inside"
                                        : "warranty-outside"
                                    }
                                  >
                                    {warrantyStatus === "مختلط" ? (
                                      <span
                                        className="mixed-warranty"
                                        onClick={() =>
                                          handleShowWarrantyDetails(
                                            request.MaintenanceRequestDetails
                                          )
                                        }
                                        style={{ cursor: "pointer" }}
                                      >
                                        مختلط
                                      </span>
                                    ) : (
                                      <p>{warrantyStatus}</p>
                                    )}
                                  </td>
                                </div>
                                <div className="info-box">
                                  <h6>الملاحظات</h6>
                                  {request.ErrorType?.trim() && (
                                    <p>{request.ErrorType}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-6 mt-0 padding">
                            <div className="client-info repair-info">
                              <div className="req-headline">
                                <div className="right-box">
                                  <div className="icon">
                                    <img src={repair} alt="repair" />
                                  </div>
                                  <h5>بيانات الصيانة</h5>
                                </div>
                              </div>
                              <div className="legend-box">
                                <div className="sub-box">
                                  <div className="info-box">
                                    <h6>تاريخ حجز الطلب</h6>
                                    <p>{request.SubmissionDate}</p>
                                  </div>
                                  <div className="info-box">
                                    <h6>تاريخ الصيانة</h6>
                                    <p>{request.MaintenanceStartDate}</p>
                                  </div>
                                </div>
                                <div className="info-box">
                                  <h6>الفني المسؤول</h6>
                                  <p>{request.TechnicianName}</p>
                                </div>
                                <div className="info-box">
                                  <h6>قطع الغيار المستخدمة</h6>
                                  <p>{request.AirCondMaintPartsStr}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-12 mt-4 mb-4">
                        {request?.Ratings?.length > 0 ? (
                          request.Ratings.map((rating) => (
                            <div
                              className="client-info rate-info"
                              key={rating.Id}
                            >
                              <div className="req-headline">
                                <div className="right-box">
                                  <div className="icon">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="26"
                                      height="26"
                                      viewBox="0 0 26 26"
                                      fill="none"
                                    >
                                      <path
                                        d="M8.6562 3.14562C8.60007 2.97264 8.46041 2.83949 8.28491 2.7915L6.07375 2.18742L4.81625 0.26996C4.61703 -0.0343226 4.11319 -0.0343226 3.91398 0.26996L2.65674 2.18742L0.44532 2.7915C0.269819 2.83949 0.130155 2.97264 0.0740266 3.14562C0.0178978 3.31885 0.0526818 3.50843 0.166257 3.65029L1.60057 5.4396L1.49121 7.73042C1.48252 7.91236 1.56605 8.08612 1.71309 8.19317C1.80664 8.2612 1.91811 8.29627 2.03037 8.29627C2.09466 8.29627 2.15949 8.28467 2.22141 8.2612L4.36511 7.44934L6.50908 8.2612C6.67904 8.3258 6.87036 8.30023 7.0174 8.19317C7.16444 8.08612 7.24797 7.91236 7.23928 7.73042L7.12992 5.4396L8.56397 3.65029C8.67755 3.50843 8.71233 3.31859 8.6562 3.14562ZM6.15992 4.92332C6.0777 5.02589 6.0358 5.15536 6.04186 5.28693L6.12171 6.95996L4.55616 6.36695C4.4945 6.34348 4.42994 6.33188 4.36511 6.33188C4.30029 6.33188 4.23573 6.34348 4.17407 6.36695L2.60878 6.9597L2.68863 5.28693C2.69495 5.15536 2.65279 5.02589 2.57057 4.92332L1.5231 3.61681L3.13792 3.17568C3.26494 3.14087 3.37482 3.06098 3.44703 2.95076L4.36511 1.55064L5.28347 2.95076C5.35567 3.06098 5.46555 3.14087 5.59257 3.17568L7.20713 3.61681L6.15992 4.92332Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M20.0159 2.72595H10.8414V3.80597H20.0159V2.72595Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M25.9524 5.42603H10.8414V6.50604H25.9524V5.42603Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M8.65632 11.9706C8.59993 11.7976 8.46053 11.6645 8.28503 11.6165L6.07387 11.0127L4.81637 9.09522C4.61716 8.79093 4.11332 8.79093 3.9141 9.09522L2.65686 11.0127L0.445442 11.6165C0.269941 11.6645 0.130541 11.7976 0.0741487 11.9706C0.0180199 12.1436 0.0528039 12.3334 0.166379 12.4753L1.60069 14.2646L1.49134 16.5554C1.48264 16.7374 1.56617 16.9111 1.71322 17.0182C1.85999 17.1247 2.05131 17.1503 2.22154 17.0862L4.36524 16.2743L6.5092 17.0862C6.57112 17.1097 6.63595 17.1213 6.70025 17.1213C6.8125 17.1213 6.92397 17.0862 7.01752 17.0182C7.16456 16.9111 7.2481 16.7374 7.2394 16.5554L7.13004 14.2646L8.56409 12.4753C8.67767 12.3334 8.71245 12.1436 8.65632 11.9706ZM6.16004 13.7483C6.07782 13.8509 6.03592 13.9804 6.04199 14.1119L6.12183 15.785L4.55628 15.1919C4.49462 15.1685 4.43006 15.1569 4.36524 15.1569C4.30041 15.1569 4.23585 15.1685 4.17419 15.1919L2.60891 15.7847L2.68875 14.1119C2.69507 13.9804 2.65291 13.8509 2.5707 13.7483L1.52322 12.4418L3.13804 12.0009C3.26506 11.9661 3.37494 11.8862 3.44715 11.776L4.36524 10.3759L5.28359 11.776C5.35579 11.8862 5.46568 11.9661 5.59269 12.0009L7.20725 12.4418L6.16004 13.7483Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M20.0159 11.3661H10.8414V12.4462H20.0159V11.3661Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M25.9524 14.0662H10.8414V15.1462H25.9524V14.0662Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M8.65632 20.7959C8.59993 20.6229 8.46053 20.4898 8.28503 20.4418L6.07387 19.8379L4.81637 17.9202C4.61716 17.6159 4.11332 17.6159 3.9141 17.9202L2.65686 19.8379L0.445442 20.4418C0.269941 20.4898 0.130541 20.6229 0.0741487 20.7959C0.0180199 20.9689 0.0528039 21.1587 0.166379 21.3006L1.60069 23.0899L1.49134 25.3807C1.48264 25.5626 1.56617 25.7364 1.71322 25.8434C1.85999 25.9505 2.05131 25.9755 2.22154 25.9115L4.36524 25.0996L6.5092 25.9115C6.57112 25.9349 6.63595 25.9465 6.70025 25.9465C6.8125 25.9465 6.92397 25.9115 7.01752 25.8434C7.16456 25.7364 7.2481 25.5626 7.2394 25.3807L7.13004 23.0899L8.56409 21.3006C8.67767 21.1587 8.71245 20.9689 8.65632 20.7959ZM6.16004 22.5736C6.07782 22.6762 6.03592 22.8056 6.04199 22.9372L6.12183 24.6102L4.55628 24.0172C4.49462 23.9938 4.43006 23.9822 4.36524 23.9822C4.30041 23.9822 4.23585 23.9938 4.17419 24.0172L2.60891 24.61L2.68875 22.9372C2.69507 22.8056 2.65291 22.6762 2.5707 22.5736L1.52322 21.2671L3.13804 20.8262C3.26506 20.7914 3.37494 20.7115 3.44715 20.6013L4.36524 19.2009L5.28359 20.6013C5.35579 20.7115 5.46568 20.7914 5.59269 20.8262L7.20725 21.2671L6.16004 22.5736Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M20.0159 20.5463H10.8414V21.6263H20.0159V20.5463Z"
                                        fill="#0D9AE1"
                                      />
                                      <path
                                        d="M25.9524 23.2463H10.8414V24.3264H25.9524V23.2463Z"
                                        fill="#0D9AE1"
                                      />
                                    </svg>
                                  </div>
                                  <h5>التقييم</h5>
                                </div>
                                <div className="left-box">
                                  <div className="icon">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="34"
                                      height="34"
                                      viewBox="0 0 34 34"
                                      fill="none"
                                    >
                                      <path
                                        d="M15.6621 3.28098C16.0859 2.33365 16.2979 1.85999 16.5931 1.71413C16.8494 1.5874 17.1504 1.5874 17.4068 1.71413C17.7019 1.85999 17.914 2.33365 18.3378 3.28098L21.7141 10.8266C21.8396 11.1066 21.9022 11.2467 21.9992 11.3539C22.0849 11.4488 22.1898 11.5247 22.3068 11.5766C22.4392 11.6353 22.5923 11.6514 22.8984 11.6836L31.1457 12.5507C32.1811 12.6596 32.6987 12.714 32.9292 12.9486C33.1293 13.1523 33.2223 13.4375 33.1806 13.7196C33.1326 14.0443 32.7459 14.3914 31.9725 15.0857L25.8119 20.6164C25.5833 20.8216 25.4689 20.9242 25.3966 21.0492C25.3325 21.1599 25.2924 21.2826 25.279 21.4096C25.2638 21.5531 25.2957 21.7031 25.3596 22.0031L27.0804 30.0845C27.2964 31.0992 27.4044 31.6065 27.2517 31.8972C27.119 32.15 26.8755 32.3262 26.5933 32.3738C26.2685 32.4285 25.8175 32.1695 24.9156 31.6513L17.7318 27.5237C17.4652 27.3705 17.3319 27.2941 17.1902 27.264C17.0648 27.2375 16.9351 27.2375 16.8097 27.264C16.668 27.2941 16.5347 27.3705 16.2681 27.5237L9.08432 31.6513C8.18243 32.1695 7.73148 32.4285 7.40664 32.3738C7.12447 32.3262 6.88088 32.15 6.74825 31.8972C6.59556 31.6065 6.70357 31.0992 6.91961 30.0845L8.64028 22.0031C8.70414 21.7031 8.73607 21.5531 8.72091 21.4096C8.70749 21.2826 8.66745 21.1599 8.60337 21.0492C8.53095 20.9242 8.41663 20.8216 8.18799 20.6164L2.02747 15.0857C1.25405 14.3914 0.867332 14.0443 0.819251 13.7196C0.777505 13.4375 0.870537 13.1523 1.07074 12.9486C1.30122 12.714 1.81892 12.6596 2.85434 12.5507L11.1016 11.6836C11.4077 11.6514 11.5607 11.6353 11.693 11.5766C11.8101 11.5247 11.915 11.4488 12.0008 11.3539C12.0977 11.2467 12.1604 11.1066 12.2857 10.8266L15.6621 3.28098Z"
                                        fill="#FFCB45"
                                      />
                                    </svg>
                                  </div>
                                  <h5>{rating.RatingValue}</h5>
                                </div>
                              </div>
                              <div className="rate-comment">
                                <p>{rating.Comments}</p>
                              </div>
                              {(rating.photo || rating.photo2) && (
                                <div className="images-box">
                                  {/* {rating.photo && (
                                    <div className="image-box">
                                      <img src={rating.photo} alt="صورة 1" />
                                    </div>
                                  )} */}
                                  {rating.photo &&
                                    rating.photo !==
                                      "https://api.generalhome.sa" && (
                                      <div className="image-box">
                                        <img
                                          src={
                                            rating.photo.startsWith("https")
                                              ? rating.photo
                                              : `data:image/jpeg;base64,${rating.photo}`
                                          }
                                          alt="صورة 1"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}
                                  {rating.photo2 &&
                                    rating.photo2 !==
                                      "https://api.generalhome.sa" && (
                                      <div className="image-box">
                                        <img
                                          src={
                                            rating.photo2.startsWith("https")
                                              ? rating.photo2
                                              : `data:image/jpeg;base64,${rating.photo2}`
                                          }
                                          alt="صورة 2"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="client-info rate-info">
                            <div className="req-headline">
                              <div className="right-box">
                                <div className="icon">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="26"
                                    height="26"
                                    viewBox="0 0 26 26"
                                    fill="none"
                                  >
                                    <path
                                      d="M8.6562 3.14562C8.60007 2.97264 8.46041 2.83949 8.28491 2.7915L6.07375 2.18742L4.81625 0.26996C4.61703 -0.0343226 4.11319 -0.0343226 3.91398 0.26996L2.65674 2.18742L0.44532 2.7915C0.269819 2.83949 0.130155 2.97264 0.0740266 3.14562C0.0178978 3.31885 0.0526818 3.50843 0.166257 3.65029L1.60057 5.4396L1.49121 7.73042C1.48252 7.91236 1.56605 8.08612 1.71309 8.19317C1.80664 8.2612 1.91811 8.29627 2.03037 8.29627C2.09466 8.29627 2.15949 8.28467 2.22141 8.2612L4.36511 7.44934L6.50908 8.2612C6.67904 8.3258 6.87036 8.30023 7.0174 8.19317C7.16444 8.08612 7.24797 7.91236 7.23928 7.73042L7.12992 5.4396L8.56397 3.65029C8.67755 3.50843 8.71233 3.31859 8.6562 3.14562ZM6.15992 4.92332C6.0777 5.02589 6.0358 5.15536 6.04186 5.28693L6.12171 6.95996L4.55616 6.36695C4.4945 6.34348 4.42994 6.33188 4.36511 6.33188C4.30029 6.33188 4.23573 6.34348 4.17407 6.36695L2.60878 6.9597L2.68863 5.28693C2.69495 5.15536 2.65279 5.02589 2.57057 4.92332L1.5231 3.61681L3.13792 3.17568C3.26494 3.14087 3.37482 3.06098 3.44703 2.95076L4.36511 1.55064L5.28347 2.95076C5.35567 3.06098 5.46555 3.14087 5.59257 3.17568L7.20713 3.61681L6.15992 4.92332Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M20.0159 2.72595H10.8414V3.80597H20.0159V2.72595Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M25.9524 5.42603H10.8414V6.50604H25.9524V5.42603Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M8.65632 11.9706C8.59993 11.7976 8.46053 11.6645 8.28503 11.6165L6.07387 11.0127L4.81637 9.09522C4.61716 8.79093 4.11332 8.79093 3.9141 9.09522L2.65686 11.0127L0.445442 11.6165C0.269941 11.6645 0.130541 11.7976 0.0741487 11.9706C0.0180199 12.1436 0.0528039 12.3334 0.166379 12.4753L1.60069 14.2646L1.49134 16.5554C1.48264 16.7374 1.56617 16.9111 1.71322 17.0182C1.85999 17.1247 2.05131 17.1503 2.22154 17.0862L4.36524 16.2743L6.5092 17.0862C6.57112 17.1097 6.63595 17.1213 6.70025 17.1213C6.8125 17.1213 6.92397 17.0862 7.01752 17.0182C7.16456 16.9111 7.2481 16.7374 7.2394 16.5554L7.13004 14.2646L8.56409 12.4753C8.67767 12.3334 8.71245 12.1436 8.65632 11.9706ZM6.16004 13.7483C6.07782 13.8509 6.03592 13.9804 6.04199 14.1119L6.12183 15.785L4.55628 15.1919C4.49462 15.1685 4.43006 15.1569 4.36524 15.1569C4.30041 15.1569 4.23585 15.1685 4.17419 15.1919L2.60891 15.7847L2.68875 14.1119C2.69507 13.9804 2.65291 13.8509 2.5707 13.7483L1.52322 12.4418L3.13804 12.0009C3.26506 11.9661 3.37494 11.8862 3.44715 11.776L4.36524 10.3759L5.28359 11.776C5.35579 11.8862 5.46568 11.9661 5.59269 12.0009L7.20725 12.4418L6.16004 13.7483Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M20.0159 11.3661H10.8414V12.4462H20.0159V11.3661Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M25.9524 14.0662H10.8414V15.1462H25.9524V14.0662Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M8.65632 20.7959C8.59993 20.6229 8.46053 20.4898 8.28503 20.4418L6.07387 19.8379L4.81637 17.9202C4.61716 17.6159 4.11332 17.6159 3.9141 17.9202L2.65686 19.8379L0.445442 20.4418C0.269941 20.4898 0.130541 20.6229 0.0741487 20.7959C0.0180199 20.9689 0.0528039 21.1587 0.166379 21.3006L1.60069 23.0899L1.49134 25.3807C1.48264 25.5626 1.56617 25.7364 1.71322 25.8434C1.85999 25.9505 2.05131 25.9755 2.22154 25.9115L4.36524 25.0996L6.5092 25.9115C6.57112 25.9349 6.63595 25.9465 6.70025 25.9465C6.8125 25.9465 6.92397 25.9115 7.01752 25.8434C7.16456 25.7364 7.2481 25.5626 7.2394 25.3807L7.13004 23.0899L8.56409 21.3006C8.67767 21.1587 8.71245 20.9689 8.65632 20.7959ZM6.16004 22.5736C6.07782 22.6762 6.03592 22.8056 6.04199 22.9372L6.12183 24.6102L4.55628 24.0172C4.49462 23.9938 4.43006 23.9822 4.36524 23.9822C4.30041 23.9822 4.23585 23.9938 4.17419 24.0172L2.60891 24.61L2.68875 22.9372C2.69507 22.8056 2.65291 22.6762 2.5707 22.5736L1.52322 21.2671L3.13804 20.8262C3.26506 20.7914 3.37494 20.7115 3.44715 20.6013L4.36524 19.2009L5.28359 20.6013C5.35579 20.7115 5.46568 20.7914 5.59269 20.8262L7.20725 21.2671L6.16004 22.5736Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M20.0159 20.5463H10.8414V21.6263H20.0159V20.5463Z"
                                      fill="#0D9AE1"
                                    />
                                    <path
                                      d="M25.9524 23.2463H10.8414V24.3264H25.9524V23.2463Z"
                                      fill="#0D9AE1"
                                    />
                                  </svg>
                                </div>
                                <h5>التقييم</h5>
                              </div>
                              <div className="left-box">
                                <div className="icon">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="34"
                                    height="34"
                                    viewBox="0 0 34 34"
                                    fill="none"
                                  >
                                    <path
                                      d="M15.6621 3.28098C16.0859 2.33365 16.2979 1.85999 16.5931 1.71413C16.8494 1.5874 17.1504 1.5874 17.4068 1.71413C17.7019 1.85999 17.914 2.33365 18.3378 3.28098L21.7141 10.8266C21.8396 11.1066 21.9022 11.2467 21.9992 11.3539C22.0849 11.4488 22.1898 11.5247 22.3068 11.5766C22.4392 11.6353 22.5923 11.6514 22.8984 11.6836L31.1457 12.5507C32.1811 12.6596 32.6987 12.714 32.9292 12.9486C33.1293 13.1523 33.2223 13.4375 33.1806 13.7196C33.1326 14.0443 32.7459 14.3914 31.9725 15.0857L25.8119 20.6164C25.5833 20.8216 25.4689 20.9242 25.3966 21.0492C25.3325 21.1599 25.2924 21.2826 25.279 21.4096C25.2638 21.5531 25.2957 21.7031 25.3596 22.0031L27.0804 30.0845C27.2964 31.0992 27.4044 31.6065 27.2517 31.8972C27.119 32.15 26.8755 32.3262 26.5933 32.3738C26.2685 32.4285 25.8175 32.1695 24.9156 31.6513L17.7318 27.5237C17.4652 27.3705 17.3319 27.2941 17.1902 27.264C17.0648 27.2375 16.9351 27.2375 16.8097 27.264C16.668 27.2941 16.5347 27.3705 16.2681 27.5237L9.08432 31.6513C8.18243 32.1695 7.73148 32.4285 7.40664 32.3738C7.12447 32.3262 6.88088 32.15 6.74825 31.8972C6.59556 31.6065 6.70357 31.0992 6.91961 30.0845L8.64028 22.0031C8.70414 21.7031 8.73607 21.5531 8.72091 21.4096C8.70749 21.2826 8.66745 21.1599 8.60337 21.0492C8.53095 20.9242 8.41663 20.8216 8.18799 20.6164L2.02747 15.0857C1.25405 14.3914 0.867332 14.0443 0.819251 13.7196C0.777505 13.4375 0.870537 13.1523 1.07074 12.9486C1.30122 12.714 1.81892 12.6596 2.85434 12.5507L11.1016 11.6836C11.4077 11.6514 11.5607 11.6353 11.693 11.5766C11.8101 11.5247 11.915 11.4488 12.0008 11.3539C12.0977 11.2467 12.1604 11.1066 12.2857 10.8266L15.6621 3.28098Z"
                                      fill="#FFCB45"
                                    />
                                  </svg>
                                </div>
                                <h5>0</h5>
                              </div>
                            </div>
                            <div className="no-rate">
                              <p>لا توجد تقيمات</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-4 h-100">
                      <div className="left-side">
                        <div className="req-headline">
                          <div className="right-box">
                            <div className="icon">
                              <img src={repairReport} alt="requestInfo" />
                            </div>
                            <h5>تقرير الصيانة</h5>
                          </div>
                          <div className="left-box">
                            <div className="icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="17"
                                viewBox="0 0 13 17"
                                fill="none"
                              >
                                <path
                                  d="M9.96876 7.4697L7.24908 10.1893V0H5.74908V10.1894L3.02941 7.46967L1.96875 8.53033L6.49904 13.0607L11.0294 8.53037L9.96876 7.4697Z"
                                  fill="#161616"
                                />
                                <path
                                  d="M12.5 15.25L0.5 15.25V16.75L12.5 16.75V15.25Z"
                                  fill="#161616"
                                />
                              </svg>
                            </div>
                            <h6 onClick={handleDownload}>تنزيل الفاتوره</h6>
                          </div>
                        </div>
                        <div className="img-holder">
                          <h5>الفاتوره</h5>
                          <TransformWrapper
                            defaultScale={1}
                            defaultPositionX={100}
                            defaultPositionY={200}
                            style={{ height: "400px" }}
                            className="w-100"
                          >
                            <TransformComponent>
                              {requestDetails.length > 0 &&
                              normalizeRequestImagePath(
                                requestDetails[0].ImagePath
                              ) ? (
                                <img
                                  className="imageDetails w-100"
                                  src={normalizeRequestImagePath(
                                    requestDetails[0].ImagePath
                                  )}
                                  alt="Receipt"
                                  ref={imageRef}
                                />
                              ) : (
                                <p className="text-center w-100">No Image</p>
                              )}
                            </TransformComponent>
                          </TransformWrapper>
                        </div>
                        <form onSubmit={submit} className="techReportFrom">
                          <label className="form-label">تقرير الصيانة</label>
                          <textarea
                            placeholder="إكتب تقرير الصيانه..."
                            onChange={(e) => setTechReport(e.target.value)}
                            value={techReport}
                          ></textarea>
                          {techReportError && (
                            <p className="text-danger">{techReportError}</p>
                          )}
                          <button className="w-100 submit-btn" type="submit">
                            <div className="icon">
                              <img src={SendingIcon} alt="SendingIcon" />
                            </div>
                            إرسال التقرير
                          </button>
                        </form>
                        <button
                          className="w-100 mt-2 print-btn"
                          onClick={handlePrint}
                        >
                          <div className="icon">
                            <img src={printing} alt="printing" />
                          </div>
                          طباعة التقرير
                        </button>
                      </div>
                    </div>
                    <div className="col-lg-4"></div>
                  </div>
                </div>
              );
            })}
            <div className="print-only" ref={componentRef}>
              {requestDetails.map((request) => (
                <div key={request.RequestId}>
                  <div className="header-printing">
                    <div className="header-right">
                      <h2>مؤسسة محمد صالح اليحيي للصناعة والتجارة</h2>
                      <p>
                        تكييف صحراوي واسبيلت _غطاسات منزلية قطع غيار -أجهزة
                        كهربائية
                      </p>
                    </div>
                    <div className="header-middle">
                      <img src={Logo} alt="Logo" />
                    </div>
                    <div className="header-left text-center">
                      <Barcode
                        value={request.FormattedRequestNumber}
                        width={1.5}
                        height={50}
                        displayValue={false}
                      />
                      <p>رقم الطلب: {request.FormattedRequestNumber}</p>
                    </div>
                  </div>
                  <div className="headline">
                    <h2>تقرير صيانة</h2>
                  </div>
                  <div className="printing-box">
                    <div className="printing-right">
                      <div className="order-info">
                        <div className="order-info-box">
                          <h5>اسم العميل:</h5>
                          <p>{request.CustomerName}</p>
                        </div>
                        <div className="order-info-box">
                          <h5>رقم الهاتف:</h5>
                          <p>{request.PhoneNumber}</p>
                        </div>
                        <div className="order-info-box">
                          <h5>المدينة:</h5>
                          <p>{request.City}</p>
                        </div>
                        <div className="order-info-box">
                          <h5>رقم الطلب:</h5>
                          <p>{request.FormattedRequestNumber}</p>
                        </div>
                        <div className="order-info-box">
                          <h5>تاريخ الحجز</h5>
                          <p>{request.SubmissionDate}</p>
                        </div>
                        <div className="order-info-box">
                          <h5>تاريخ الصيانة</h5>
                          <p>{request.MaintenanceStartDate}</p>
                        </div>
                      </div>
                      <div className="order-status">
                        <div className="order-status-box">
                          <h5>حالة الضمان</h5>
                          <p>{request.WarrantyStatusName}</p>
                        </div>
                        <div className="order-status-box">
                          <h5>حالة الطلب</h5>
                          {request.CurrentStatus === "طلبات جديده" ? (
                            <p className="newReq">جديد</p>
                          ) : request.CurrentStatus ===
                            "الطلبات التي تم الموافقه عليها" ? (
                            <p className="approvedReq">تحت المراجعه</p>
                          ) : request.CurrentStatus === "الطلبات المرفوضه" ? (
                            <p className="rejectedReq">مرفوض</p>
                          ) : request.CurrentStatus ===
                            "المواعيد التي تم جدولتها" ? (
                            <p className="inProgress">جاري التنفيذ</p>
                          ) : request.CurrentStatus ===
                            "المواعيد التي تم إلغائها" ? (
                            <p className="rejectedReq">تم إلغاء الطلب</p>
                          ) : (
                            <p className="maintainceDone">تم تنفيذ الطلب</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="printing-left">
                      {requestDetails.length > 0 &&
                      requestDetails[0].ImagePath &&
                      requestDetails[0].ImagePath.trim() !==
                        "https://api.generalhome.sa/Resources/Images" ? (
                        <img
                          className="imageDetails"
                          src={requestDetails[0].ImagePath}
                          alt="Receipt"
                        />
                      ) : (
                        <p className="text-center">No Image</p>
                      )}
                    </div>
                  </div>
                  <div className="printing-row printing-box">
                    <div className="order-info">
                      <div className="order-info-box">
                        <h5>أسم المنتج</h5>
                        <p>{request.ACType}</p>
                      </div>
                      <div className="order-info-box">
                        <h5>وصف المشكلة</h5>
                        {request.ErrorType?.trim() && (
                          <p>{request.ErrorType}</p>
                        )}
                      </div>
                      <div className="order-info-box">
                        <h5>قطع الغيار المستخدمة:</h5>
                        <p>{request.AirCondMaintPartsStr}</p>
                      </div>
                      <div className="order-info-box">
                        <h5>تقرير الفني:</h5>
                        <p> {request.TechnicianNotes}</p>
                      </div>
                    </div>
                  </div>
                  <div className="printing-footer">
                    <div className="tech-name">
                      <h5>الفني المسؤل:</h5>
                      <p> {request.TechnicianName}</p>
                    </div>
                    <div className="signiture">
                      <h5>توقيع الفني المسؤول</h5>
                      <p>........................</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {warrantyDetailsPopup && (
          <div className="warranty-details-popup">
            <div className="warranty-details-card">
              <div className="warranty-details-header">
                <h3>تفاصيل حالة الضمان</h3>
                <button
                  onClick={handleCloseWarrantyDetails}
                  className="close-btn"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="warranty-details-body">
                <div className="warranty-status-summary">
                  <span className="summary-label">الحالة الإجمالية:</span>
                  <span
                    className={`summary-value ${
                      getWarrantyStatus(warrantyDetailsPopup) === "داخل الضمان"
                        ? "inside"
                        : "outside"
                    }`}
                  >
                    {getWarrantyStatus(warrantyDetailsPopup)}
                  </span>
                </div>
                <div className="warranty-items-container">
                  {warrantyDetailsPopup.map((item, index) => (
                    <div key={index} className="warranty-item">
                      <div className="ac-type">
                        <i className="fas fa-snowflake"></i>
                        <span>{item.ACTypeName}</span>
                      </div>
                      <div
                        className={`warranty-status ${
                          item.WarrantyStatusName === "داخل الضمان"
                            ? "inside"
                            : "outside"
                        }`}
                      >
                        {item.WarrantyStatusName}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="warranty-details-footer">
                <button
                  onClick={handleCloseWarrantyDetails}
                  className="close-button"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default RequestDetails;
